import React, { useEffect, useMemo, useState } from "react";

import {
  Activity,
  ArrowRight,
  HeartPulse,
  Info,
  LayoutGrid,
  Loader2,
} from "lucide-react";
import { createPortal } from "react-dom";

import CompactActionCard from "./CompactActionCard";
import {
  clusterFromAccountHealth,
  type PortfolioClusterKey,
} from "./portfolioClusterStyles";
import {
  buildHeaderInfoTooltips,
  headerInfoIconClass,
  headerTooltipClass,
} from "./portfolioHealthInfo";
import PortfolioMapAccountSummary from "./PortfolioMapAccountSummary";
import PortfolioMapComparisonModal from "./PortfolioMapComparisonModal";
import PortfolioMapComparisonPanel from "./PortfolioMapComparisonPanel";
import {
  buildScatterQuadrantCornerLabels,
  PM_TRANSITIONS,
} from "./portfolioMapGuides";
import PortfolioMapMatrix, {
  type MatrixLensConfig,
} from "./PortfolioMapMatrix";
import PortfolioMapScatter, { type LensConfig } from "./PortfolioMapScatter";
import {
  type EnrichedMapAccount,
  bhClassFromScore,
  getCompactSignalSelection,
  RL_PRIORITY_LABEL,
} from "./portfolioMapVisualHelpers";
import { usePortfolioMapLenses } from "./usePortfolioMapLenses";
import { useSignalPresentation } from "./useSignalPresentation";
import { useHealthScoreThresholds } from "../../Hooks/useHealthScoreThresholds";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../intelligence-briefing-library/components/ui/tooltip";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import type { PortfolioAccountRow } from "../../Store/Reducers/MyPortfolioOverviewSlice";
import { fetchPortfolioMap } from "../../Store/Reducers/PortfolioMapSlice";
import {
  dismissSignal,
  snoozeSignal,
} from "../../Store/Reducers/SignalFeedSlice";

interface Props {
  /** Optional account id to deep-link + flash-highlight on arrival in
   * Actions (see ActionsTab.tsx's highlightAccountId). */
  onSeeAllActions: (accountId?: string, signalKey?: string) => void;
  withOrg: (path: string) => string;
  onOpenAssess: (account: { id: string; name: string }) => void;
  overviewAccounts: PortfolioAccountRow[];
  onGoToPriorities: (subTab: "account" | "relationship") => void;
  onGoToPortfolioGoals: () => void;
  onSetWorkingSet: (accountIds: string[]) => void;
  /** An externally-set working set (from another tab's "Review..." link, or
   * a prior Portfolio Map comparison carried across tab switches) — shown
   * as a banner and used to scope which accounts are plottable, mirroring
   * Actions/Priorities/Goals' own working-set banner+filter. */
  workingSetAccountIds?: string[] | null;
  onClearWorkingSet?: () => void;
  /** When set, lens tabs / terminology / compare controls render here
   * (beside Account Priorities → List | Maps) instead of above the chart. */
  lensBarEl?: HTMLDivElement | null;
  /** When true, never render the in-chart lens bar (parent hosts it). */
  externalLensBar?: boolean;
  /** Deep-link from List — select these accounts on mount/update. */
  focusAccountIds?: string[] | null;
  /** Prefer Business Health vs Account Health lens group when focusing. */
  focusHealthCategory?: "business_health" | "account_health" | "both" | null;
  onFocusConsumed?: () => void;
}

/** x/y scatter lenses (BH×Fit, Net Signal×Fit, BH×AH, AH Risk×Opportunity).
 * The other 2 of the mock's 6 lenses (AH-BH 3×3, AH Posture × AL) are
 * matrix-layout chart
 * types, not x/y scatters — see MATRIX_LENSES below, rendered via
 * PortfolioMapMatrix instead of PortfolioMapScatter. Labels/axis text/
 * quadrant copy below are ported verbatim from the mock's PM_LENS_NAMES/
 * PM_LENS_AXIS/quadrant labels (my-accounts-portfolio-v4.html lines
 * 6631-6647, 2035-2111 area for the lens bar, and PortfolioMapTab's own
 * original quadrant blurbs where the mock doesn't spell out a per-quadrant
 * blurb separately from its PM_GUIDES description). */
const LENSES: LensConfig[] = [
  {
    key: "bh_fit",
    label: "BH × Fit",
    // Mock's PM_LENS_AXIS.bhfit, verbatim (Y stated before X, &nbsp;·&nbsp; separator).
    axisDescription:
      "Y = Business Health · X = Low → High Strategic Relevance (Fit)",
    xAxisTitle: "Strategic Relevance (Fit)",
    getX: a => a.fit,
    xMin: 0,
    xMax: 100,
    xMid: 50,
    xLowLabel: "Low Fit",
    xHighLabel: "High Fit",
    yLabel: "BH",
    yAxisTitle: "Business Health",
    getY: a => a.bh,
    yMid: 50,
    yMin: 0,
    yMax: 100,
    yLowLabel: "Low BH",
    yHighLabel: "High BH",
    quadrants: {
      topRight: {
        label: "Priority Growth",
        blurb:
          "High Fit + high BH — invest aggressively in AL and RL advancement.",
      },
      topLeft: {
        label: "Steady State",
        blurb:
          "Low Fit + high BH — maintain selectively and re-evaluate strategic potential.",
      },
      bottomRight: {
        label: "Urgent Intervention",
        blurb:
          "High Fit + BH concern — protect the relationship while the business recovers.",
      },
      bottomLeft: {
        label: "Monitor",
        blurb:
          "Low Fit + BH concern — periodic check-in; limit proactive investment.",
      },
    },
    quadrantCornerLabels: buildScatterQuadrantCornerLabels("bh_fit"),
  },
  {
    // Net = Opp − Risk from public-data scoreStrip risk/opportunity
    // (account_public_data), not BH and not KPI RCS/OCS — keeps this lens
    // distinct from BH × Fit and AH Risk × Opportunity.
    key: "net_signal_fit",
    label: "Net Investment × Fit",
    axisDescription:
      "Y = Net Investment from public Risk/Opp (Opp − Risk) · X = Low → High Fit",
    xAxisTitle: "Strategic Relevance (Fit)",
    getX: a => a.fit,
    xMin: 0,
    xMax: 100,
    xMid: 50,
    xLowLabel: "Low Fit",
    xHighLabel: "High Fit",
    yLabel: "Net Signal",
    yAxisTitle: "Net Investment Signal (Opp − Risk)",
    getY: a => a.net_investment_signal,
    yMid: 0,
    yMin: -100,
    yMax: 100,
    yLowLabel: "Risk",
    yHighLabel: "Opportunity",
    quadrants: {
      topRight: {
        label: "Invest Aggressively",
        blurb:
          "High Fit + positive Net Signal — conditions favour rapid RL advancement.",
      },
      topLeft: {
        label: "Selective Opportunity",
        blurb:
          "Low Fit + positive signal — pursue opportunistically, not as a top priority.",
      },
      bottomRight: {
        label: "Protect Urgently",
        blurb:
          "High Fit + risk overhang — defend the account and preserve relationship investment.",
      },
      bottomLeft: {
        label: "Deprioritize",
        blurb: "Low Fit + risk overhang — minimize spend and watch for change.",
      },
    },
    quadrantCornerLabels: buildScatterQuadrantCornerLabels("net_signal_fit"),
  },
  {
    key: "bh_ah",
    label: "BH × AH",
    axisDescription: "Y = Business Health · X = Account Health (AH)",
    xAxisTitle: "Account Health (AH)",
    // Mock: ah > 0 is scored; 0/null park in the AH UNSCORED gutter.
    getX: a => (a.ah != null && a.ah > 0 ? a.ah : null),
    xMin: 0,
    xMax: 100,
    xMid: 50,
    xLowLabel: "Low AH",
    xHighLabel: "High AH",
    xUnknownLabel: "AH UNSCORED",
    yLabel: "BH",
    yAxisTitle: "Business Health",
    getY: a => a.bh,
    yMid: 50,
    yMin: 0,
    yMax: 100,
    yLowLabel: "Low BH",
    yHighLabel: "High BH",
    quadrants: {
      topRight: {
        label: "Healthy All Around",
        blurb:
          "Strong BH + high AH — expansion-ready; advance AL and RL goals.",
      },
      topLeft: {
        label: "Perception Gap",
        blurb:
          "Strong BH + low AH — external runway exists while internal KPI recovery is needed.",
      },
      bottomRight: {
        label: "Resilient Account",
        blurb:
          "Low BH + high AH — protect strong execution from external headwinds.",
      },
      bottomLeft: {
        label: "Double Red Flag",
        blurb: "Low BH + low AH — compound risk requiring immediate attention.",
      },
    },
    quadrantCornerLabels: buildScatterQuadrantCornerLabels("bh_ah"),
  },
  {
    // Birds Eye Risk–Opportunity map: X = Opportunity, Y = Risk severity
    // (high at top). Scores come from KPI `account_ro_scores` (OCS + inverted
    // RCS), not public-data briefing risk/opportunity.
    key: "cluster",
    label: "AH Risk × Opportunity",
    axisDescription: "Y = Risk Score · X = Opportunity Score",
    xAxisTitle: "Opportunity",
    getX: a => a.opportunity,
    xMin: 0,
    xMax: 100,
    xMid: 50,
    xLowLabel: "Low Opp",
    xHighLabel: "High Opp",
    yLabel: "Risk",
    yAxisTitle: "Risk",
    getY: a => a.risk,
    yMid: 50,
    yMin: 0,
    yMax: 100,
    yLowLabel: "Low Risk",
    yHighLabel: "High Risk",
    quadrants: {
      topLeft: {
        label: "Triage",
        blurb:
          "High risk + low opportunity — intervene and stabilize urgently.",
      },
      topRight: {
        label: "Defend",
        blurb:
          "High risk + high opportunity — protect the account while preserving upside.",
      },
      bottomLeft: {
        label: "Monitor",
        blurb:
          "Low risk + low opportunity — monitor lightly; automate touchpoints.",
      },
      bottomRight: {
        label: "Expand",
        blurb:
          "Low risk + high opportunity — enable growth and advance AL/RL goals.",
      },
    },
    quadrantCornerLabels: (() => {
      const corners = buildScatterQuadrantCornerLabels("cluster");
      if (!corners) return undefined;
      return {
        topLeft: { ...corners.topLeft, subtitle: "Exec: Intervene" },
        topRight: { ...corners.topRight, subtitle: "Exec: Protect" },
        bottomLeft: { ...corners.bottomLeft, subtitle: "Exec: Monitor" },
        bottomRight: { ...corners.bottomRight, subtitle: "Exec: Enable" },
      };
    })(),
  },
];

function resolveAhCluster(a: EnrichedMapAccount): PortfolioClusterKey {
  return a.ah_cluster ?? clusterFromAccountHealth(a.ah);
}

/** BH×AH: ported verbatim from mock's renderPMAHBHMatrix()/
 * PM_MATRIX_CLASSIFIERS.ahbh3x3 (~6228-6263, 5636-5642). */
const AHBH_CELL_META: Record<
  string,
  { label: string; color: string; fill: string }
> = {
  "high:concern": {
    label: "Air cover / resilient",
    color: "#b7591a",
    fill: "#fff8ee",
  },
  "high:stable": {
    label: "Mixed conditions",
    color: "#64748b",
    fill: "#f7fafc",
  },
  "high:strong": {
    label: "Best zone",
    color: "#15704a",
    fill: "#eef9f3",
  },
  "low:concern": {
    label: "Most urgent",
    color: "#b42318",
    fill: "#fef0f0",
  },
  "low:stable": {
    label: "Stabilize + intervene",
    color: "#b7591a",
    fill: "#fff8ee",
  },
  "low:strong": {
    label: "Recovery runway",
    color: "#2563eb",
    fill: "#f0f7ff",
  },
  "unscored:concern": {
    label: "No KPI score · BH concern",
    color: "#b42318",
    fill: "#fef0f0",
  },
  "unscored:stable": {
    label: "No KPI score · BH stable",
    color: "#64748b",
    fill: "#f7fafc",
  },
  "unscored:strong": {
    label: "No KPI score · BH strong",
    color: "#15704a",
    fill: "#eef9f3",
  },
};

const AHBH_MATRIX_LENS: MatrixLensConfig = {
  key: "ahbh3x3",
  label: "AH-BH Matrix",
  axisDescription: "Rows = Account Health tier · Columns = Business Health",
  rows: [
    { key: "high", label: "High AH", sub: "Monitor / Expand" },
    { key: "low", label: "Low AH", sub: "Triage / Defend" },
    { key: "unscored", label: "Unscored", sub: "No KPI data" },
  ],
  cols: [
    { key: "concern", label: "BH Concern", sub: "Low external health" },
    { key: "stable", label: "BH Stable", sub: "Mixed external health" },
    { key: "strong", label: "BH Strong", sub: "High external health" },
  ],
  xAxisLabel: "Business Health",
  yAxisLabel: "Account Health",
  columnPosition: "bottom",
  classify: a => {
    const cluster = resolveAhCluster(a);
    const row =
      cluster === "unscored"
        ? "unscored"
        : cluster === "grow" || cluster === "expand"
          ? "high"
          : "low";
    const bhClass = a.bh_class;
    const col =
      bhClass === "strong" || bhClass === "stable" ? bhClass : "concern";
    return { row, col };
  },
  cellMeta: (row, col) => AHBH_CELL_META[`${row}:${col}`],
  badge: a => (a.ah && a.ah > 0 ? String(Math.round(a.ah)) : "—"),
  waitingLabel: "WAITING FOR HEALTH DATA",
};

/** Cluster × AL: ported verbatim from mock's renderPMClusterALMatrix()/
 * PM_MATRIX_CLASSIFIERS.clusteral (~6265-6302, 5643-5648). Accounts with
 * missing AH or AL land in the availability-frame gutters (AL-unknown column,
 * AH-unscored row, or both in the corner) instead of the waiting shelf. */
const CLUSTER_AL_CELL_META: Record<
  string,
  { label: string; color: string; fill: string }
> = {
  "triage:low": {
    label: "Critical — no foundation",
    color: "#b42318",
    fill: "#fef0f0",
  },
  "triage:mid": {
    label: "Defend the foundation",
    color: "#c2410c",
    fill: "#fff3ed",
  },
  "triage:high": {
    label: "Alignment-led recovery",
    color: "#b7591a",
    fill: "#fdf6ec",
  },
  "defend:low": {
    label: "Drift risk",
    color: "#b7591a",
    fill: "#fff8ee",
  },
  "defend:mid": {
    label: "Defend / on track",
    color: "#64748b",
    fill: "#f7fafc",
  },
  "defend:high": {
    label: "Execution review",
    color: "#2563eb",
    fill: "#f0f7ff",
  },
  "growth:low": {
    label: "Monitor alignment gap",
    color: "#b7591a",
    fill: "#fff8ee",
  },
  "growth:mid": {
    label: "Alignment developing",
    color: "#15704a",
    fill: "#eef9f3",
  },
  "growth:high": {
    label: "Realized partnership",
    color: "#0f766e",
    fill: "#eaf7f3",
  },
  "growth:unknown": {
    label: "AL ?",
    color: "#64748b",
    fill: "#f5f7fa",
  },
  "defend:unknown": {
    label: "AL ?",
    color: "#64748b",
    fill: "#f5f7fa",
  },
  "triage:unknown": {
    label: "AL ?",
    color: "#64748b",
    fill: "#f5f7fa",
  },
  "unscored:low": {
    label: "No KPI score · AL known",
    color: "#64748b",
    fill: "#f5f7fa",
  },
  "unscored:mid": {
    label: "No KPI score · AL known",
    color: "#64748b",
    fill: "#f5f7fa",
  },
  "unscored:high": {
    label: "No KPI score · AL known",
    color: "#64748b",
    fill: "#f5f7fa",
  },
  "unscored:unknown": {
    label: "AL ? · no KPI",
    color: "#64748b",
    fill: "#eef1f5",
  },
};

function alBand(al: number): "low" | "mid" | "high" {
  return al <= 1 ? "low" : al <= 3 ? "mid" : "high";
}

function clusterRowFromAh(a: EnrichedMapAccount): string | null {
  const cluster = resolveAhCluster(a);
  if (cluster === "unscored") return null;
  return cluster === "grow" || cluster === "expand" ? "growth" : cluster;
}

const CLUSTER_AL_MATRIX_LENS: MatrixLensConfig = {
  key: "cluster_al",
  label: "AH × AL",
  axisDescription: "Rows = Account cluster · Columns = Alignment Level (AL)",
  rows: [
    { key: "growth", label: "Monitor", sub: "Monitor + Expand" },
    { key: "defend", label: "Defend", sub: "Protect" },
    { key: "triage", label: "Triage", sub: "Urgent" },
    {
      key: "unscored",
      label: "AH UNSCORED",
      sub: "No KPI score",
    },
  ],
  cols: [
    { key: "unknown", label: "AL ?", sub: "Not assessed" },
    { key: "low", label: "Low AL", sub: "AL 0–1" },
    { key: "mid", label: "Mid AL", sub: "AL 2–3" },
    { key: "high", label: "High AL", sub: "AL 4–5" },
  ],
  xAxisLabel: "Alignment Level (AL)",
  yAxisLabel: "Account Health",
  columnPosition: "bottom",
  peripheralLayout: {
    unknownColKey: "unknown",
    unscoredRowKey: "unscored",
    gutterPx: 14,
    unknownColW: 96,
    unscoredRowH: 96,
    unknownColLabel: "AL ?",
    unknownColSub: "Not assessed",
    unscoredRowLabel: "AH UNSCORED",
    unscoredRowSub: "No KPI score",
  },
  classify: a => {
    const row = clusterRowFromAh(a);
    const hasAl = a.al != null;
    if (row == null && !hasAl) {
      return { row: "unscored", col: "unknown" };
    }
    if (row == null && hasAl) {
      return { row: "unscored", col: alBand(a.al!) };
    }
    if (row != null && !hasAl) {
      return { row, col: "unknown" };
    }
    return { row: row!, col: alBand(a.al!) };
  },
  cellMeta: (row, col) =>
    CLUSTER_AL_CELL_META[`${row}:${col}`] ?? {
      label: "",
      color: "#64748b",
      fill: "#f5f7fa",
    },
  badge: a => (a.al != null ? `AL ${a.al}` : "AL ?"),
  waitingLabel: "WAITING / UNPLACED",
};

const MATRIX_LENSES: MatrixLensConfig[] = [
  AHBH_MATRIX_LENS,
  CLUSTER_AL_MATRIX_LENS,
];

/** Business Health lenses — BH × Fit family. */
const BH_LENS_BUTTONS = [
  { key: "bh_fit", label: "BH × Fit" },
  { key: "net_signal_fit", label: "Net Investment × Fit" },
] as const;

/** Account Health lenses — AH-only matrices / scatters. */
const AH_LENS_BUTTONS = [
  { key: "cluster", label: "AH Risk × Opportunity" },
  { key: CLUSTER_AL_MATRIX_LENS.key, label: "AH × AL" },
] as const;

/** Both — lenses that combine Business Health and Account Health. */
const BOTH_LENS_BUTTONS = [
  { key: AHBH_MATRIX_LENS.key, label: "AH-BH Matrix" },
  { key: "bh_ah", label: "BH × AH" },
] as const;

const ALL_LENS_BUTTONS = [
  ...BH_LENS_BUTTONS,
  ...AH_LENS_BUTTONS,
  ...BOTH_LENS_BUTTONS,
];

type MapHealthCategory = "business_health" | "account_health" | "both";

function categoryForLens(key: string): MapHealthCategory {
  if (BH_LENS_BUTTONS.some(l => l.key === key)) return "business_health";
  if (BOTH_LENS_BUTTONS.some(l => l.key === key)) return "both";
  return "account_health";
}

/** Mock's .pm-lens-key terminology line under the lens buttons. */
export const LENS_KEY_TERMS: [string, string][] = [
  ["BH", "Business Health"],
  ["AH", "Account Health"],
  ["AL", "Account Level"],
  ["Fit", "Strategic Relevance"],
];

/** BH×AH stays selectable even when no account has AH — the scatter parks
 * missing-AH accounts in the AH UNSCORED gutter instead of locking the lens. */

const PortfolioMapTab: React.FC<Props> = ({
  onSeeAllActions,
  withOrg,
  onOpenAssess,
  overviewAccounts,
  onGoToPriorities,
  onGoToPortfolioGoals,
  onSetWorkingSet,
  workingSetAccountIds,
  onClearWorkingSet,
  lensBarEl,
  externalLensBar = false,
  focusAccountIds = null,
  focusHealthCategory = null,
  onFocusConsumed,
}) => {
  const dispatch = useAppDispatch();
  const {
    accounts: rawAccounts,
    loading,
    error,
  } = useAppSelector(state => state.portfolioMap);
  const { cards: signalCards, actionInFlightId } = useAppSelector(
    state => state.signalFeed
  );
  const { businessHealth: bhThresholds, accountHealth: ahThresholds } =
    useHealthScoreThresholds();
  const { signalsVisible } = useSignalPresentation();
  const { mapsEnabled, isLensVisible } = usePortfolioMapLenses();

  const visibleBhLenses = useMemo(
    () => BH_LENS_BUTTONS.filter(l => isLensVisible(l.key)),
    [isLensVisible]
  );
  const visibleAhLenses = useMemo(
    () => AH_LENS_BUTTONS.filter(l => isLensVisible(l.key)),
    [isLensVisible]
  );
  const visibleBothLenses = useMemo(
    () => BOTH_LENS_BUTTONS.filter(l => isLensVisible(l.key)),
    [isLensVisible]
  );
  const visibleAllLenses = useMemo(
    () => [...visibleBhLenses, ...visibleAhLenses, ...visibleBothLenses],
    [visibleBhLenses, visibleAhLenses, visibleBothLenses]
  );

  const visibleLensesForCategory = (category: MapHealthCategory) => {
    if (category === "business_health") return visibleBhLenses;
    if (category === "both") return visibleBothLenses;
    return visibleAhLenses;
  };

  const headerInfoTooltips = useMemo(
    () => buildHeaderInfoTooltips(bhThresholds, ahThresholds),
    [bhThresholds, ahThresholds]
  );

  const overviewById = useMemo(() => {
    const map = new Map<
      string,
      { al: number | null; bh: number | null; name: string }
    >();
    for (const a of overviewAccounts) {
      map.set(a.customer_id, {
        al: a.al_level,
        bh: a.business_health,
        name: a.customer_name || "",
      });
    }
    return map;
  }, [overviewAccounts]);
  const enrichedAccounts: EnrichedMapAccount[] = useMemo(
    () =>
      rawAccounts.map(a => {
        const ov = overviewById.get(a.account_id);
        // Prefer List overview BH so Maps matches Account Priorities scores
        // (75/71/73) even when portfolio-map briefing fetch is empty.
        const bh =
          ov?.bh != null && Number.isFinite(ov.bh)
            ? ov.bh
            : a.bh != null && Number.isFinite(a.bh)
              ? a.bh
              : null;
        const accountName =
          (a.account_name && a.account_name !== "Unknown"
            ? a.account_name
            : null) ||
          ov?.name ||
          a.account_name ||
          "Unknown";
        const ahCluster = clusterFromAccountHealth(
          a.ah,
          a.account_id,
          ahThresholds
        );
        return {
          ...a,
          account_name: accountName,
          al: ov?.al ?? null,
          bh,
          bh_class: bhClassFromScore(bh, bhThresholds),
          ah_cluster: ahCluster,
        };
      }),
    [rawAccounts, overviewById, ahThresholds, bhThresholds]
  );
  // Always plot the full portfolio on this tab. `workingSetAccountIds` only
  // scopes Actions / Priorities / Goals after "Review … →" — applying it
  // here hid every other dot when the user returned from those tabs.
  const accounts = enrichedAccounts;

  const [lensKey, setLensKey] = useState<string>(
    () => ALL_LENS_BUTTONS[0]?.key ?? "bh_fit"
  );
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>(
    // Restore the comparison set after "Review actions →" round-trips:
    // local selection is lost on unmount, but the shared working set still
    // holds those ids — re-seed so the same dots stay selected while the
    // full portfolio is plotted again.
    () => workingSetAccountIds?.slice() ?? []
  );
  // Gates the comparison modal — matches the mock: a manual/shift-click
  // selection never auto-opens a comparison view, it only reveals the
  // "Compare N accounts" button (pmDotClick/updatePMCompareButton,
  // my-accounts-portfolio-v4.html ~6620-6647, ~6749-6759). The modal itself
  // (openPMComparison/renderPMComparisonModal, ~6655-6696, ~8830-8845) is a
  // read-only table snapshot — closing it does not clear the selection.
  const [comparisonModalOpen, setComparisonModalOpen] = useState(false);
  const scatterLens = useMemo(
    () => LENSES.find(l => l.key === lensKey),
    [lensKey]
  );
  const matrixLens = useMemo(
    () => MATRIX_LENSES.find(l => l.key === lensKey),
    [lensKey]
  );
  const lens = scatterLens ?? LENSES[0];

  useEffect(() => {
    void dispatch(fetchPortfolioMap());
    // signal-feed is fetched once at the page level (index.tsx), not
    // per-tab-mount — see ActionsTab.tsx's comment on the same pattern for
    // why: re-fetching here on every visit to this tab would silently
    // resurrect an in-session dismiss/snooze from the compact rail below,
    // since an "acknowledged" firing is still legitimately returned by the
    // API until its underlying condition resolves.
  }, [dispatch]);

  useEffect(() => {
    if (!focusAccountIds || focusAccountIds.length === 0) return;
    setSelectedAccountIds(focusAccountIds);
    if (focusHealthCategory) {
      const next =
        visibleLensesForCategory(focusHealthCategory)[0] ?? visibleAllLenses[0];
      if (next) setLensKey(next.key);
    }
    onFocusConsumed?.();
  }, [
    focusAccountIds,
    focusHealthCategory,
    onFocusConsumed,
    visibleAhLenses,
    visibleBhLenses,
    visibleBothLenses,
    visibleAllLenses,
  ]);

  // If the current lens was hidden by Izba Admin, fall back to the first visible.
  useEffect(() => {
    if (!mapsEnabled || visibleAllLenses.length === 0) return;
    if (isLensVisible(lensKey)) return;
    setLensKey(visibleAllLenses[0].key);
  }, [mapsEnabled, lensKey, isLensVisible, visibleAllLenses]);

  // Single selection keeps the existing per-account actions detail card;
  // 2+ selections switch the right panel to the comparison view (matches
  // the mock: a lone click opens actions, shift-click/drag builds a
  // comparison set — see PortfolioMapScatter.tsx).
  const selectedAccountId =
    selectedAccountIds.length === 1 ? selectedAccountIds[0] : null;
  const selectedAccount = useMemo(
    () => accounts.find(a => a.account_id === selectedAccountId) ?? null,
    [accounts, selectedAccountId]
  );
  const selectedAccountCards = useMemo(
    () =>
      selectedAccountId == null
        ? []
        : signalCards.filter(c => c.account_id === selectedAccountId),
    [signalCards, selectedAccountId]
  );
  // Mock getAccountSignalsSorted + getCompactSignalSelection (v4 ~6950-6976):
  // sort by tier urgency, then pick one card per tier (or up to 3 if a
  // single tier). Full list remains available via "See all … Actions".
  const compactSelection = useMemo(() => {
    const tierRank: Record<string, number> = {
      critical: 0,
      warning: 1,
      opp: 2,
      maint: 3,
    };
    const sorted = [...selectedAccountCards].sort((a, b) => {
      const tierDelta = (tierRank[a.tier] ?? 3) - (tierRank[b.tier] ?? 3);
      if (tierDelta) return tierDelta;
      return a.signal_key.localeCompare(b.signal_key);
    });
    return getCompactSignalSelection(sorted);
  }, [selectedAccountCards]);
  const compactLimitNote = useMemo(() => {
    if (!compactSelection.hiddenCount) return "";
    if (compactSelection.tierCount === 1) {
      const tier = compactSelection.shown[0]?.tier ?? "active";
      return `Showing the top ${compactSelection.shown.length} of ${selectedAccountCards.length} ${tier} actions.`;
    }
    return `Showing the top action in each urgency tier · ${compactSelection.hiddenCount} more in Actions.`;
  }, [compactSelection, selectedAccountCards.length]);
  const comparisonAccounts = useMemo(
    () =>
      selectedAccountIds.length < 2
        ? []
        : selectedAccountIds
            .map(id => accounts.find(a => a.account_id === id))
            .filter((a): a is EnrichedMapAccount => a != null),
    [accounts, selectedAccountIds]
  );
  const isComparing = comparisonAccounts.length >= 2;

  // NOTE: deliberately not auto-syncing the map's own local selection into
  // the shared `workingSetAccountIds` on every shift-click. The working set
  // is pushed explicitly at "Review actions/priorities →" / "See all
  // portfolio actions →" (and the comparison modal's callbacks). The map
  // always plots the full portfolio — it must not filter by working set, or
  // returning from Actions would hide every non-selected dot.

  const clearComparisonSelection = () => {
    setSelectedAccountIds([]);
    setComparisonModalOpen(false);
    if (isComparing) onClearWorkingSet?.();
  };

  // Ported from mock's pmComputeStats() (my-accounts-portfolio-v4.html
  // ~6692-6751), one branch per lens, verbatim labels. The mock's bhfit
  // branch's 4th stat — "accounts need AL assessment" (`!a.al`) — now uses
  // `EnrichedMapAccount.al`, joined client-side from `my-portfolio/overview`
  // (see portfolioMapVisualHelpers.ts) since `GET /api/v1/portfolio-map`
  // itself has no `al` field.
  const stats = useMemo((): { num: number; label: string }[] => {
    if (matrixLens) return [];
    if (lens.key === "bh_fit") {
      const priorityGrowth = accounts.filter(
        a => a.fit != null && a.bh != null && a.fit >= 50 && a.bh >= 50
      ).length;
      const highRL = accounts.filter(a => a.fit != null && a.fit >= 70).length;
      const needsAlAssessment = accounts.filter(a => a.al == null).length;
      return [
        {
          num: priorityGrowth,
          label: "accounts in Priority Growth quadrant (High Fit + High BH)",
        },
        { num: highRL, label: "accounts are High RL Priority (Fit ≥ 70)" },
        { num: needsAlAssessment, label: "accounts need AL assessment" },
        { num: accounts.length, label: "total accounts in portfolio" },
      ];
    }
    if (lens.key === "bh_ah") {
      const healthy = accounts.filter(
        a => a.bh != null && a.ah != null && a.bh > 50 && a.ah > 50
      ).length;
      const doubleRed = accounts.filter(
        a => a.bh != null && a.ah != null && a.bh < 50 && a.ah < 50
      ).length;
      return [
        { num: healthy, label: "Healthy All Around (High BH + High AH)" },
        { num: doubleRed, label: "Double Red Flag accounts (Low BH + Low AH)" },
        {
          num: accounts.length - healthy - doubleRed,
          label: "mixed or perception-gap accounts",
        },
      ];
    }
    if (lens.key === "cluster") {
      const scored = accounts.filter(
        a => a.risk != null && a.opportunity != null
      );
      const triageDefend = scored.filter(a => (a.risk as number) >= 50).length;
      const expandDelegate = scored.filter(a => (a.risk as number) < 50).length;
      return [
        {
          num: triageDefend,
          label: "accounts in Triage/Defend (high risk)",
        },
        {
          num: expandDelegate,
          label: "accounts in Expand/Monitor (low risk)",
        },
        { num: accounts.length, label: "total accounts in portfolio" },
      ];
    }
    const posNet = accounts.filter(
      a => a.net_investment_signal != null && a.net_investment_signal > 0
    ).length;
    const riskOvhg = accounts.filter(
      a => a.net_investment_signal != null && a.net_investment_signal < 0
    ).length;
    return [
      { num: posNet, label: "accounts: positive net investment signal" },
      { num: riskOvhg, label: "accounts: risk overhang (Risk > Opp)" },
    ];
  }, [accounts, lens.key, matrixLens]);

  const transitions = useMemo(() => {
    const raw = PM_TRANSITIONS[lensKey] ?? [];
    // Keep "Check attention signals →" even when the Signal service is off,
    // as long as the target map lens is visible.
    return raw.filter(t => !t.lens || isLensVisible(t.lens));
  }, [lensKey, isLensVisible]);

  const healthCategory = categoryForLens(lensKey);

  const selectHealthCategory = (category: MapHealthCategory) => {
    if (category === healthCategory) return;
    const next = visibleLensesForCategory(category)[0];
    if (next) setLensKey(next.key);
  };

  const renderLensSubs = (
    lenses: readonly { key: string; label: string }[],
    groupLabel: string
  ) => (
    <div
      className="inline-flex items-center gap-px pr-1"
      role="group"
      aria-label={groupLabel}
    >
      {lenses.map((l, i) => (
        <React.Fragment key={l.key}>
          {i > 0 && (
            <span
              className="select-none px-0.5 text-[11px] font-normal text-gray-300"
              aria-hidden
            >
              |
            </span>
          )}
          <button
            type="button"
            aria-pressed={lensKey === l.key}
            className={`whitespace-nowrap rounded border-none px-1.5 py-0.5 text-[11px] font-semibold transition-colors ${
              lensKey === l.key
                ? "bg-[#f5edfc] text-brand"
                : "bg-transparent text-gray-500 hover:bg-black/[0.04] hover:text-gray-700"
            }`}
            onClick={() => setLensKey(l.key)}
          >
            {l.label}
          </button>
        </React.Fragment>
      ))}
    </div>
  );

  const showBhCategory = visibleBhLenses.length > 0;
  const showAhCategory = visibleAhLenses.length > 0;
  const showBothCategory = visibleBothLenses.length > 0;
  const categoryParts: React.ReactNode[] = [];
  if (showBhCategory) {
    categoryParts.push(
      <div
        key="bh"
        className={`inline-flex items-center gap-0.5 rounded-md px-0.5 py-px transition-colors ${
          healthCategory === "business_health" ? "bg-white shadow-sm" : ""
        }`}
      >
        <div className="inline-flex items-center gap-1 whitespace-nowrap px-1">
          <button
            type="button"
            role="tab"
            aria-selected={healthCategory === "business_health"}
            className={`inline-flex items-center gap-1.5 rounded border-none bg-transparent px-1.5 py-1.5 text-xs font-semibold transition-colors ${
              healthCategory === "business_health"
                ? "text-[#7a36c4]"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => selectHealthCategory("business_health")}
          >
            <Activity className="h-3.5 w-3.5 opacity-85" aria-hidden />
            Business Health
          </button>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={headerInfoIconClass}
                aria-label="About Business Health"
                onClick={e => e.stopPropagation()}
              >
                <Info className="h-2 w-2" strokeWidth={2.5} aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="start"
              sideOffset={8}
              className={headerTooltipClass}
            >
              {headerInfoTooltips.businessHealth}
            </TooltipContent>
          </Tooltip>
        </div>
        {healthCategory === "business_health" &&
          renderLensSubs(visibleBhLenses, "Business Health lenses")}
      </div>
    );
  }
  if (showAhCategory) {
    categoryParts.push(
      <div
        key="ah"
        className={`inline-flex items-center gap-0.5 rounded-md px-0.5 py-px transition-colors ${
          healthCategory === "account_health" ? "bg-white shadow-sm" : ""
        }`}
      >
        <div className="inline-flex items-center gap-1 whitespace-nowrap px-1">
          <button
            type="button"
            role="tab"
            aria-selected={healthCategory === "account_health"}
            className={`inline-flex items-center gap-1.5 rounded border-none bg-transparent px-1.5 py-1.5 text-xs font-semibold transition-colors ${
              healthCategory === "account_health"
                ? "text-[#7a36c4]"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => selectHealthCategory("account_health")}
          >
            <HeartPulse className="h-3.5 w-3.5 opacity-85" aria-hidden />
            Account Health
          </button>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={headerInfoIconClass}
                aria-label="About Account Health"
                onClick={e => e.stopPropagation()}
              >
                <Info className="h-2 w-2" strokeWidth={2.5} aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="start"
              sideOffset={8}
              className={headerTooltipClass}
            >
              {headerInfoTooltips.accountHealth}
            </TooltipContent>
          </Tooltip>
        </div>
        {healthCategory === "account_health" &&
          renderLensSubs(visibleAhLenses, "Account Health lenses")}
      </div>
    );
  }
  if (showBothCategory) {
    categoryParts.push(
      <div
        key="both"
        className={`inline-flex items-center gap-0.5 rounded-md px-0.5 py-px transition-colors ${
          healthCategory === "both" ? "bg-white shadow-sm" : ""
        }`}
      >
        <div className="inline-flex items-center gap-1 whitespace-nowrap px-1">
          <button
            type="button"
            role="tab"
            aria-selected={healthCategory === "both"}
            className={`inline-flex items-center gap-1.5 rounded border-none bg-transparent px-1.5 py-1.5 text-xs font-semibold transition-colors ${
              healthCategory === "both"
                ? "text-[#7a36c4]"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => selectHealthCategory("both")}
          >
            <LayoutGrid className="h-3.5 w-3.5 opacity-85" aria-hidden />
            Both
          </button>
        </div>
        {healthCategory === "both" &&
          renderLensSubs(visibleBothLenses, "Both health lenses")}
      </div>
    );
  }

  const lensBar = (
    <TooltipProvider delayDuration={150}>
      <>
        <div
          className="inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-lg border border-gray-200 bg-gray-100 p-0.5"
          role="tablist"
          aria-label="Portfolio Map lenses"
        >
          {categoryParts.map((part, idx) => (
            <React.Fragment key={(part as React.ReactElement).key ?? idx}>
              {idx > 0 && (
                <div
                  className="mx-0.5 my-1 w-px shrink-0 self-stretch bg-gray-200"
                  aria-hidden
                />
              )}
              {part}
            </React.Fragment>
          ))}
        </div>

        {isComparing && (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setComparisonModalOpen(true)}
              className="rounded-md border border-brand px-2.5 py-1 text-[11.5px] font-bold text-brand hover:bg-brand-faint"
            >
              Compare({comparisonAccounts.length})
            </button>
            <button
              type="button"
              aria-label="Clear selected accounts"
              onClick={clearComparisonSelection}
              className="text-[11px] font-bold text-gray-500 hover:text-brand hover:underline"
            >
              Clear
            </button>
          </div>
        )}
      </>
    </TooltipProvider>
  );

  const lensBarPortal = lensBarEl ? createPortal(lensBar, lensBarEl) : null;
  const showInternalLensBar = !externalLensBar && !lensBarEl;

  if (!mapsEnabled || visibleAllLenses.length === 0) {
    return (
      <div className="flex min-h-[240px] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
        Portfolio Maps are not available for this organization.
      </div>
    );
  }

  if (loading && accounts.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {lensBarPortal}
        {showInternalLensBar && (
          <div className="flex flex-wrap items-center gap-3">{lensBar}</div>
        )}
        <div className="flex min-h-[320px] items-center justify-center text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          <span className="ml-2 text-sm">Loading portfolio map…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        {lensBarPortal}
        {showInternalLensBar && (
          <div className="flex flex-wrap items-center gap-3">{lensBar}</div>
        )}
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Couldn&apos;t load the portfolio map: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {lensBarPortal}
      {showInternalLensBar && (
        <div className="flex flex-wrap items-center gap-3">{lensBar}</div>
      )}

      {/* .pm-wrap: display:flex; gap:10px; align-items:flex-start (mock ~1226-1229).
          .pm-scatter-panel: flex:0 0 78% (mock ~1230); right column stacks
          the persistent "Suggested next view" box above the contextual
          panel (mock .pm-right-column). */}
      <div className="flex flex-col items-start gap-[10px] lg:flex-row">
        <div className="w-full min-w-0 lg:basis-[78%] lg:grow-0 lg:shrink-0">
          {matrixLens ? (
            <PortfolioMapMatrix
              accounts={accounts}
              lens={matrixLens}
              selectedAccountIds={selectedAccountIds}
              onSelectedAccountIdsChange={setSelectedAccountIds}
              signalsVisible={signalsVisible}
            />
          ) : (
            <PortfolioMapScatter
              accounts={accounts}
              lens={lens}
              selectedAccountIds={selectedAccountIds}
              onSelectedAccountIdsChange={setSelectedAccountIds}
              signalsVisible={signalsVisible}
            />
          )}
        </div>

        <div className="flex w-full min-w-0 flex-1 flex-col gap-2">
          {transitions.length > 0 && (
            <div className="flex flex-col items-center gap-1.5 rounded-lg border border-[rgba(118,74,188,.22)] bg-brand-faint px-[9px] py-2">
              <div className="w-full text-center text-[10px] font-extrabold uppercase tracking-[.06em] text-gray-400">
                Suggested next view
              </div>
              {transitions.map(t => (
                <button
                  key={t.lens}
                  type="button"
                  onClick={() => setLensKey(t.lens)}
                  className="flex w-full items-center justify-center gap-1 rounded-md border border-[rgba(118,74,188,.25)] bg-white px-3 py-1.5 text-center text-[11.5px] font-bold leading-none text-brand hover:border-brand"
                >
                  {t.label.replace(/\s*→\s*$/, "")}
                  <ArrowRight
                    className="h-3.5 w-3.5 shrink-0 translate-y-px"
                    strokeWidth={2.25}
                    aria-hidden
                  />
                </button>
              ))}
            </div>
          )}

          <div className="min-h-[390px] w-full rounded-[10px] border border-gray-200 bg-white p-[11px]">
            {isComparing ? (
              <PortfolioMapComparisonPanel
                title="Manual comparison"
                accounts={comparisonAccounts}
                overviewAccounts={overviewAccounts}
                onRemove={id => {
                  const next = selectedAccountIds.filter(x => x !== id);
                  setSelectedAccountIds(next);
                  if (next.length < 2) onClearWorkingSet?.();
                }}
                onClear={clearComparisonSelection}
                onReviewActions={
                  signalsVisible
                    ? () => {
                        onSetWorkingSet(
                          comparisonAccounts.map(a => a.account_id)
                        );
                        onSeeAllActions();
                      }
                    : undefined
                }
                onReviewPriorities={() => {
                  onSetWorkingSet(comparisonAccounts.map(a => a.account_id));
                  onGoToPriorities("account");
                }}
                onReviewGoals={() => {
                  onSetWorkingSet(comparisonAccounts.map(a => a.account_id));
                  onGoToPortfolioGoals();
                }}
              />
            ) : selectedAccount ? (
              <div className="flex flex-col gap-2.5">
                <div>
                  <p className="truncate text-sm font-bold text-gray-900">
                    {selectedAccount.account_name}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Fit{" "}
                    {selectedAccount.fit != null
                      ? Math.round(selectedAccount.fit)
                      : "—"}{" "}
                    · BH{" "}
                    {selectedAccount.bh != null
                      ? Math.round(selectedAccount.bh)
                      : "—"}
                    {selectedAccount.bh != null &&
                    selectedAccount.bh_class &&
                    selectedAccount.bh_class !== "no_data"
                      ? ` (${
                          {
                            strong: "Strong",
                            stable: "Stable",
                            concern: "Concern",
                          }[selectedAccount.bh_class] ??
                          selectedAccount.bh_class
                        })`
                      : ""}{" "}
                    · AH{" "}
                    {selectedAccount.ah != null && selectedAccount.ah > 0
                      ? Math.round(selectedAccount.ah)
                      : "—"}
                    {selectedAccount.rl_priority
                      ? ` · RL Priority: ${RL_PRIORITY_LABEL[selectedAccount.rl_priority] ?? selectedAccount.rl_priority}`
                      : ""}
                  </p>
                </div>
                <div className="h-px bg-gray-100" />
                {!signalsVisible ? (
                  <PortfolioMapAccountSummary
                    account={selectedAccount}
                    overview={
                      overviewAccounts.find(
                        a => a.customer_id === selectedAccount.account_id
                      ) ?? null
                    }
                  />
                ) : selectedAccountCards.length === 0 ? (
                  <p className="text-xs text-gray-400">
                    No active signals for this account.
                  </p>
                ) : (
                  <>
                    <div className="flex max-h-[420px] flex-col gap-2 overflow-y-auto">
                      {compactSelection.shown.map(card => (
                        <CompactActionCard
                          key={card.signal_firing_id}
                          card={card}
                          busy={actionInFlightId === card.signal_firing_id}
                          onSnooze={firingId =>
                            void dispatch(snoozeSignal({ firingId }))
                          }
                          onDismiss={firingId =>
                            void dispatch(dismissSignal(firingId))
                          }
                          withOrg={withOrg}
                          onOpenAssess={onOpenAssess}
                          onViewInActions={signalKey => {
                            onSetWorkingSet([selectedAccount.account_id]);
                            onSeeAllActions(
                              selectedAccount.account_id,
                              signalKey
                            );
                          }}
                        />
                      ))}
                    </div>
                    {compactLimitNote ? (
                      <p className="text-[11px] leading-snug text-gray-400">
                        {compactLimitNote}
                      </p>
                    ) : null}
                  </>
                )}
                {signalsVisible && (
                  <button
                    type="button"
                    onClick={() => {
                      onSetWorkingSet([selectedAccount.account_id]);
                      onSeeAllActions(selectedAccount.account_id);
                    }}
                    className="mt-1 text-left text-xs font-semibold text-purple-700 hover:underline"
                  >
                    See all portfolio actions →
                  </button>
                )}
              </div>
            ) : (
              // Mock's State 1 (#pm-rp-state1, HTML 2079-2086 / renderPMRightPanelState1
              // JS 6753-6774): lens name, PM_LENS_AXIS text, pm-rp-stat rows
              // (large bold number + label), pm-rp-hint footer — no divider
              // rules in the mock between these (the two `h-px` dividers below
              // were a paraphrase; mock has none here).
              <div className="flex flex-col gap-2.5">
                <p className="text-sm font-bold text-gray-900">
                  {matrixLens?.label ?? lens.label}
                </p>
                <p className="text-xs text-gray-500">
                  {matrixLens?.axisDescription ?? lens.axisDescription}
                </p>
                <div className="mt-1 flex flex-col gap-1.5">
                  {stats.map((s, i) => (
                    <div
                      key={i}
                      className="text-[11.5px] leading-[1.35] text-gray-500"
                    >
                      <span className="mr-1 text-[17px] font-extrabold leading-none text-gray-900">
                        {s.num}
                      </span>
                      {s.label}
                    </div>
                  ))}
                </div>
                <p className="mt-auto border-t border-gray-200 pt-2 text-xs italic text-gray-400">
                  {signalsVisible
                    ? "Click a quadrant to select accounts · Click an account for actions"
                    : "Click a quadrant to select accounts"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <PortfolioMapComparisonModal
        open={comparisonModalOpen}
        accounts={comparisonAccounts}
        overviewAccounts={overviewAccounts}
        subtitleLabel="Manual comparison"
        onClose={() => setComparisonModalOpen(false)}
        onReviewActions={
          signalsVisible
            ? () => {
                setComparisonModalOpen(false);
                onSetWorkingSet(comparisonAccounts.map(a => a.account_id));
                onSeeAllActions();
              }
            : undefined
        }
        onReviewPriorities={() => {
          setComparisonModalOpen(false);
          onSetWorkingSet(comparisonAccounts.map(a => a.account_id));
          onGoToPriorities("account");
        }}
        onReviewGoals={() => {
          setComparisonModalOpen(false);
          onSetWorkingSet(comparisonAccounts.map(a => a.account_id));
          onGoToPortfolioGoals();
        }}
      />
    </div>
  );
};

export default PortfolioMapTab;
